import React from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  AccountBalance as AccountIcon,
  Psychology as PsychologyIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { formatCurrency, formatPercent, formatDuration, identifyImpulsiveTrades } from '../utils';

const Insights = ({ analysis, trades }) => {
  // Get comprehensive analysis
  const detailedAnalysis = analysis;

  // Identify trading style
  let tradingStyle = 'Unknown';
  let styleDescription = '';
  if (detailedAnalysis.avgDuration < 5) {
    tradingStyle = 'Scalping';
    styleDescription = 'Short durations - focus on quick FVG retraces';
  } else if (detailedAnalysis.avgDuration < 60) {
    tradingStyle = 'Day Trading';
    styleDescription = 'Medium durations - capture intraday moves';
  } else if (detailedAnalysis.avgDuration < 1440) {
    tradingStyle = 'Intraday Swing';
    styleDescription = 'Hold for several hours to capture larger moves';
  } else {
    tradingStyle = 'Swing Trading';
    styleDescription = 'Multi-day holds - capture trend movements';
  }

  // You got it right - positive feedback
  let positives = [];
  if (detailedAnalysis.winRate > 70) {
    positives.push({
      text: 'High win rate - strong setup identification in FVG retraces.',
      icon: <ThumbUpIcon color="success" />,
    });
  }
  if (detailedAnalysis.avgPoints > 20) {
    positives.push({
      text: 'Good point capture - effective targeting of opposite direction moves.',
      icon: <TrendingUpIcon color="success" />,
    });
  }
  if (detailedAnalysis.profitFactor > 2) {
    positives.push({
      text: 'Excellent profit factor - your winners significantly outweigh your losers.',
      icon: <CheckCircleIcon color="success" />,
    });
  }
  if (detailedAnalysis.consecutiveWins > 5) {
    positives.push({
      text: `Impressive streak of ${detailedAnalysis.consecutiveWins} consecutive wins - maintain this consistency!`,
      icon: <TrendingUpIcon color="success" />,
    });
  }
  if (detailedAnalysis.zeroFeeSwap) {
    positives.push({
      text: 'Zero fees/swaps - Cost-efficient execution',
      icon: <AccountIcon color="success" />,
    });
  }
  if (positives.length === 0) {
    positives.push({
      text: 'Consistent logging - keep building your journal for better patterns.',
      icon: <InfoIcon color="info" />,
    });
  }

  // Areas to improve with details
  let improvements = [];
  if (detailedAnalysis.qtyStd > 0.5) {
    improvements.push({
      text: `Inconsistent sizing (std dev: ${(detailedAnalysis.qtyStd || 0).toFixed(2)}) - standardize to 1.0 lots to reduce risk; this could prevent overexposure in volatile NAS100 sessions.`,
      icon: <WarningIcon color="warning" />,
    });
  }
  if (detailedAnalysis.lowPnlCount > 0) {
    improvements.push({
      text: ` ${detailedAnalysis.lowPnlCount} low P&L trades (<$10) - improve by filtering setups with <10 points potential; focus on stronger FVG zones during high liquidity hours.`,
      icon: <TrendingDownIcon color="warning" />,
    });
  }
  if (detailedAnalysis.scalpCount > trades.length / 2) {
    improvements.push({
      text: 'High scalp frequency - ensure exits are timed better to avoid slippage; consider adding 1-min chart confirmations.',
      icon: <WarningIcon color="warning" />,
    });
  }
  if (detailedAnalysis.maxDrawdown > Math.abs(detailedAnalysis.totalPnl * 0.3)) {
    improvements.push({
      text: `Significant drawdown (${formatCurrency(detailedAnalysis.maxDrawdown)}) - review risk management during volatile periods.`,
      icon: <TrendingDownIcon color="warning" />,
    });
  }
  if (detailedAnalysis.avgRiskReward < 1.5) {
    improvements.push({
      text: `Low risk/reward ratio (${(detailedAnalysis.avgRiskReward || 0).toFixed(2)}) - aim for minimum 2:1 setups to improve profitability.`,
      icon: <WarningIcon color="warning" />,
    });
  }

  // Prop firm recommendations based on style
  let propRecs = [];
  if (tradingStyle.includes('Scalping')) {
    propRecs.push({
      firm: 'The5ers',
      description: 'Trader-friendly for NAS100 scalpers with low latency and competitive spreads; aim for $100k account with your performance.',
      accountSize: '$50k-$200k',
      icon: <AccountIcon color="primary" />,
    });
    propRecs.push({
      firm: 'FTMO',
      description: 'Risk management focus, up to $200k accounts for scalpers; perfect for scaling your IFVG strategy.',
      accountSize: '$50k-$200k',
      icon: <AccountIcon color="primary" />,
    });
    propRecs.push({
      firm: 'DNA Funded',
      description: 'Flexible, low-cost challenges for indices like NAS100; start with $50k+ to match your style.',
      accountSize: '$25k-$100k',
      icon: <AccountIcon color="primary" />,
    });
  } else if (tradingStyle.includes('Day Trading')) {
    propRecs.push({
      firm: 'Atmos Funded',
      description: 'High profit split, unlimited challenge time; good for day traders with $100k accounts.',
      accountSize: '$50k-$300k',
      icon: <AccountIcon color="primary" />,
    });
    propRecs.push({
      firm: 'FundedNext',
      description: 'Diverse markets, suitable for day traders; consider $100k for optimal performance.',
      accountSize: '$25k-$250k',
      icon: <AccountIcon color="primary" />,
    });
  } else {
    propRecs.push({
      firm: 'FundedNext',
      description: 'Diverse markets, suitable for swing traders; consider $100k for longer holds.',
      accountSize: '$25k-$250k',
      icon: <AccountIcon color="primary" />,
    });
    propRecs.push({
      firm: 'True Forex Funding',
      description: 'Flexible evaluation, good for swing strategies; offers $100k+ accounts.',
      accountSize: '$25k-$150k',
      icon: <AccountIcon color="primary" />,
    });
  }

  // Performance insights
  const performanceInsights = [
    {
      title: 'Best Trade',
      value: detailedAnalysis.bestTrade ? formatCurrency(detailedAnalysis.bestTrade.pnl) : '$0.00',
      description: detailedAnalysis.bestTrade ? 
        `${detailedAnalysis.bestTrade.symbol} ${detailedAnalysis.bestTrade.side} on ${new Date(detailedAnalysis.bestTrade.closeDate).toLocaleDateString()}` : 
        'No trades yet',
      positive: detailedAnalysis.bestTrade && detailedAnalysis.bestTrade.pnl > 0,
    },
    {
      title: 'Worst Trade',
      value: detailedAnalysis.worstTrade ? formatCurrency(detailedAnalysis.worstTrade.pnl) : '$0.00',
      description: detailedAnalysis.worstTrade ? 
        `${detailedAnalysis.worstTrade.symbol} ${detailedAnalysis.worstTrade.side} on ${new Date(detailedAnalysis.worstTrade.closeDate).toLocaleDateString()}` : 
        'No trades yet',
      positive: detailedAnalysis.worstTrade && detailedAnalysis.worstTrade.pnl > 0,
    },
    {
      title: 'Max Drawdown',
      value: formatCurrency(detailedAnalysis.maxDrawdown),
      description: `${formatPercent(detailedAnalysis.maxDrawdownPercent)} of peak equity`,
      positive: false,
    },
    {
      title: 'Profit Factor',
      value: (detailedAnalysis.profitFactor || 0).toFixed(2),
      description: detailedAnalysis.profitFactor > 2 ? 'Excellent profitability' :
                  detailedAnalysis.profitFactor > 1.5 ? 'Good profitability' :
                  'Needs improvement',
      positive: detailedAnalysis.profitFactor > 1.5,
    },
  ];

  // Market condition insights
  const marketConditionInsights = Object.entries(detailedAnalysis.marketConditions || {})
    .map(([condition, count]) => ({
      condition,
      count,
      winRate: (() => {
        const conditionTrades = trades.filter(t => t.marketCondition === condition);
        const wins = conditionTrades.filter(t => t.pnl >= 0).length;
        return conditionTrades.length > 0 ? (wins / conditionTrades.length) * 100 : 0;
      })(),
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Trading Insights
      </Typography>

      <Grid container spacing={3}>
        {/* Trading Style Identification */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              avatar={<PsychologyIcon color="primary" />}
              title="Trading Style Analysis"
              subheader="Identified based on your average trade duration"
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label={`${tradingStyle} (${styleDescription})`} 
                  color="primary" 
                  size="large"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your average trade duration is {formatDuration(detailedAnalysis.avgDuration)}.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              avatar={<BarChartIcon color="primary" />}
              title="Performance Metrics"
            />
            <CardContent>
              <Grid container spacing={2}>
                {performanceInsights.map((insight, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        borderLeft: `4px solid ${insight.positive ? '#4caf50' : '#f44336'}`,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        {insight.title}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: insight.positive ? 'success.main' : 'error.main',
                          fontWeight: 'bold',
                        }}
                      >
                        {insight.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {insight.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Positives */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<ThumbUpIcon color="success" />}
              title="What You're Doing Right"
              subheader="Positive aspects of your trading"
            />
            <CardContent>
              <List>
                {positives.map((positive, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {positive.icon}
                    </ListItemIcon>
                    <ListItemText primary={positive.text} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Areas for Improvement */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<WarningIcon color="warning" />}
              title="Areas to Improve"
              subheader="Opportunities to enhance your performance"
            />
            <CardContent>
              <List>
                {improvements.map((improvement, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {improvement.icon}
                    </ListItemIcon>
                    <ListItemText primary={improvement.text} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Prop Firm Recommendations */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              avatar={<AccountIcon color="primary" />}
              title="Prop Firm Recommendations"
              subheader={`Based on your ${tradingStyle.toLowerCase()} style`}
            />
            <CardContent>
              <Grid container spacing={2}>
                {propRecs.map((rec, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>
                        {rec.firm}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {rec.description}
                      </Typography>
                      <Chip 
                        label={`Account: ${rec.accountSize}`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Recommendation:</strong> With your performance metrics, aim for a {tradingStyle.includes('Scalping') ? '$100k+' : '$75k+'} account to scale your trading.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Market Condition Analysis */}
        {marketConditionInsights.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                avatar={<InfoIcon color="info" />}
                title="Market Condition Performance"
                subheader="Your performance across different market conditions"
              />
              <CardContent>
                <Grid container spacing={2}>
                  {marketConditionInsights.map((condition, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {condition.condition}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {condition.count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Trades
                        </Typography>
                        <Typography variant="body2">
                          Win Rate: {formatPercent(condition.winRate)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Impulsive Trades Analysis */}
        {trades && trades.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                avatar={<PsychologyIcon color="warning" />}
                title="Impulsive Trades Analysis"
                subheader="Trades identified as potentially impulsive based on your notes"
              />
              <CardContent>
                {(() => {
                  const impulsiveTrades = identifyImpulsiveTrades(trades);
                  if (impulsiveTrades.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No impulsive trades detected. Keep up the good work!
                      </Typography>
                    );
                  }
                  
                  return (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Found {impulsiveTrades.length} potentially impulsive trades ({(trades.length > 0 ? ((impulsiveTrades.length / trades.length) * 100).toFixed(1) : '0.0')}% of total).
                      </Typography>
                      <Grid container spacing={2}>
                        {impulsiveTrades.slice(0, 5).map((trade, index) => (
                          <Grid item xs={12} key={index}>
                            <Paper sx={{ p: 2 }}>
                              <Typography variant="subtitle2">
                                Trade #{trade.tradeId} - {new Date(trade.closeDate).toLocaleDateString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {trade.notes}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                        {impulsiveTrades.length > 5 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              ... and {impulsiveTrades.length - 5} more impulsive trades
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
         Journal © 2025 | Built by ZenoLaunch Team
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Focus on the setup, trust the process | journal To Keep Your Data Performance -ICT
        </Typography>
      </Box>
    </Paper>
  );
};

export default Insights;